import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useLoading } from "../../../../context/LoadingContext";
import { Auth } from "aws-amplify";
import { jwtDecode } from "jwt-decode";
import {
  fetchData,
  postQuery,
} from "../../../Requirement/services/RequirementServices";
import { v4 as uuidv4 } from "uuid";
import AlertModal from "../../../Requirement/components/common/AlerModel";
import {
  Box,
  Typography,
  Avatar,
  Paper,
} from "@mui/material";
import { styled } from "@mui/material/styles";

// Utility function to format date as 'Aug 15, 2024'
const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    console.warn(`Invalid date string: ${dateString}`);
    return "";
  }
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

// Utility function to format time as 'Aug 15, 3:45 PM'
const formatTime = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    console.warn(`Invalid date string: ${dateString}`);
    return "";
  }
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

// Styled Components
const MessageBubble = styled(Paper)(({ isOwn }) => ({
  padding: "12px 16px",
  maxWidth: "75%",
  alignSelf: isOwn ? "flex-end" : "flex-start",
  backgroundColor: isOwn ? "#e3f2fd" : "#fff",
  borderRadius: "12px",
  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  position: "relative",
  border: isOwn ? "1px solid #90caf9" : "1px solid #e0e0e0",
  color: "#333",
}));

const MessageHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  marginBottom: "8px",
}));

const UserName = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: "14px",
  color: "#333",
}));

const UserEmail = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  fontSize: "12px",
  color: "#9CA3AF", // light grey
  marginTop: "2px",
}));

const Timestamp = styled(Typography)(({ theme }) => ({
  fontSize: "11px",
  color: "#666",
}));

const MessageText = styled(Typography)(({ theme }) => ({
  fontSize: "14px",
  color: "#333",
  wordWrap: "break-word",
  whiteSpace: "pre-wrap",
}));

const AmMessage = ({ filter }) => {
  const navigate = useNavigate();
  const messageAreaRef = useRef(null);
  const { setLoading } = useLoading();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [conversationMessages, setConversationMessages] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [activeUser, setActiveUser] = useState("");
  const [buyerInfo, setBuyerInfo] = useState([]);
  const [textmessage, setTextMessage] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [menuOpen, setMenuOpen] = useState(null);

  const [modal, setModal] = useState({
    show: false,
    type: "info",
    title: "",
    message: "",
    nextAction: null,
  });

  const handleModalClose = () => {
    const action = modal.nextAction;
    setModal((prev) => ({ ...prev, show: false, nextAction: null }));
    if (action) action();
  };

  console.log("Filter value:", filter);

  const getBuyerInfo = useCallback(async (buyerEmail) => {
    try {
      const response = await fetchData({
        target: "listAMTPLUsers",
        userOrder: "AMTPLUserID",
        rowStart: 0,
        pageSize: 100,
        filterTerms: { EmailID: buyerEmail },
      });
      console.log("Buyer info response:", response);
      setBuyerInfo(response.items || []);
    } catch (error) {
      console.error("Error loading buyer info:", error);
      setBuyerInfo([]);
    }
  }, []);

  useEffect(() => {
    const checkUser = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const token = user.signInUserSession.idToken.jwtToken;
        const decodedToken = jwtDecode(token);
        const groups = decodedToken["cognito:groups"] || [];
        if (!groups.includes("AMTPL_BUYERS")) {
          await Auth.signOut();
          navigate("/login/buyer");
        } else {
          console.log('cog user.attributes identities@@',user.attributes.identities);
          setActiveUser(user.attributes.email);
          getBuyerInfo(user.attributes.email);
        }
      } catch (error) {
        console.error("Error checking user:", error);
        await Auth.signOut();
        navigate("/login/buyer");
      }
    };
    checkUser();
  }, [navigate, getBuyerInfo]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const response = await fetchData({
          target: "listAMTPLUsers",
          userOrder: "AMTPLUserID",
          rowStart: 0,
          pageSize: 100,
          filterTerms: {
            UserType: "Admin",
          },
        });
        console.log("listAMTPLUsers response:", response);

        const data = response.items || [];
        const initialUsers = data
          .filter((user) => user.EmailID !== activeUser)
          .map((user) => ({
            id: user.AMTPLUserID,
            sender: `${user.FirstName || ""} ${user.LastName || ""}`,
            emailID: user.EmailID,
            text: "",
            date: "",
            companyName: user.CompanyName || "Unknown Company",
            firstName: user.FirstName || "",
            lastName: user.LastName || "",
            updatedAt: user.UpdatedAt || user.CreatedAt || "",
          }));

        const usersWithMessages = await Promise.all(
          initialUsers.map(async (user) => {
            try {
              const convResponse = await fetchData({
                target: "listConversations",
                userOrder: "LastMessageDate",
                orderDirection: "DESC",
                rowStart: 0,
                pageSize: 1,
                FirstAMTPLUserID: activeUser,
                SecondAMTPLUserID: user.emailID,
              });
              console.log(`Conversation for ${user.emailID}:`, convResponse);

              const conversation = convResponse?.items?.[0];
              const rawDate =
                conversation?.LastMessageDate || user.updatedAt || "";
              console.log(`User ${user.emailID} rawDate:`, rawDate);

              let unreadCount = 0;
              if (conversation) {
                const msgResponse = await fetchData({
                  target: "listMessages",
                  userOrder: "CreatedAt",
                  orderDirection: "ASC",
                  rowStart: 0,
                  pageSize: 1000,
                  filterTerms: {
                    ConversationID: conversation.ConversationID,
                    ReceiverUserID: activeUser,
                    IsRead: 0,
                    IsArchive: 0,
                  },
                });
                console.log(
                  `Messages for ${user.emailID} (before filtering):`,
                  msgResponse.items
                );
                const filteredMessages =
                  msgResponse.items?.filter(
                    (msg) => msg.SenderUserID !== activeUser
                  ) || [];
                unreadCount = filteredMessages.length;
                console.log(`Unread count for ${user.emailID}:`, unreadCount);
              }

              return {
                ...user,
                text: conversation?.LastMessage || "",
                date: formatDate(rawDate),
                rawDate,
                unreadCount,
              };
            } catch (error) {
              console.error(
                `Error fetching conversation for ${user.emailID}:`,
                error
              );
              return {
                ...user,
                text: "",
                date: formatDate(user.updatedAt),
                rawDate: user.updatedAt || "",
                unreadCount: 0,
              };
            }
          })
        );

        const sortedUsers = usersWithMessages.sort((a, b) => {
          const dateA = a.rawDate ? new Date(a.rawDate) : new Date(0);
          const dateB = b.rawDate ? new Date(b.rawDate) : new Date(0);
          if (dateB.getTime() !== dateA.getTime()) {
            return dateB - dateA;
          }
          return b.unreadCount - a.unreadCount;
        });

        console.log("Sorted usersWithMessages:", sortedUsers);
        setUsers(sortedUsers);

        const filteredForUnread =
          filter === "Unread"
            ? sortedUsers.filter((user) => user.unreadCount > 0)
            : sortedUsers;
        setFilteredUsers(filteredForUnread);
        console.log("Filtered users:", filteredForUnread);

        if (filteredForUnread.length > 0 && !selectedUser) {
          const firstUser = filteredForUnread[0];
          setSelectedUser(firstUser);
          setTextMessage(true);
          fetchConversation(firstUser);
        } else if (selectedUser) {
          const updatedSelectedUser = filteredForUnread.find(
            (user) => user.id === selectedUser.id
          );
          if (updatedSelectedUser) {
            setSelectedUser(updatedSelectedUser);
            setTextMessage(true);
            fetchConversation(updatedSelectedUser);
          } else {
            const firstUser = filteredForUnread[0];
            setSelectedUser(firstUser);
            setTextMessage(true);
            fetchConversation(firstUser);
          }
        } else {
          setSelectedUser(null);
          setTextMessage(false);
          setConversationMessages([]);
        }
      } catch (error) {
        console.error("Error fetching users:", error);
        setUsers([]);
        setFilteredUsers([]);
      } finally {
        setLoading(false);
      }
    };

    if (activeUser) {
      fetchUsers();
    }
  }, [activeUser, filter]);

  useEffect(() => {
    let filtered = users;
    if (filter === "Unread") {
      filtered = filtered.filter((user) => user.unreadCount > 0);
    }
    filtered = filtered.filter((user) =>
      `${user.sender} ${user.emailID} ${user.companyName}`
        .toLowerCase()
        .includes(searchQuery.toLowerCase())
    );
    setFilteredUsers(filtered);
    console.log("Filtered users after search:", filtered);

    if (selectedUser && !filtered.find((u) => u.id === selectedUser.id)) {
      setConversationMessages([]);
      setTextMessage(false);
      setSelectedUser(null);
    }
  }, [searchQuery, users, filter, selectedUser]);

  const handleUserClick = (user) => {
    setSelectedUser(user);
    setTextMessage(true);
    fetchConversation(user);
  };

  const fetchConversation = async (user) => {
    try {
      setLoading(true);
      const idResponse = await fetchData({
        target: "listConversations",
        userOrder: "LastMessageDate",
        orderDirection: "DESC",
        rowStart: 0,
        pageSize: 10,
        FirstAMTPLUserID: activeUser,
        SecondAMTPLUserID: user.emailID,
      });
      console.log(`Conversation fetch for ${user.emailID}:`, idResponse);

      const conversationID = idResponse?.items?.[0]?.ConversationID;
      if (!conversationID) {
        setConversationMessages([]);
        return;
      }

      const varArchive = filter === "Archive" ? 1 : 0;
      const msgResponse = await fetchData({
        target: "listMessages",
        userOrder: "CreatedAt",
        orderDirection: "ASC",
        rowStart: 0,
        pageSize: 1000,
        filterTerms: {
          ConversationID: conversationID,
          IsArchive: varArchive,
        },
      });
      console.log(`Messages for conversation ${conversationID}:`, msgResponse);

      const msgs = msgResponse.items || [];
      const formattedMessages = msgs.map((msg) => ({
        id: msg.MessageID,
        sender: msg.SenderUserID,
        text: msg.Content,
        date: formatTime(msg.CreatedAt),
        ConversationID: msg.ConversationID,
        SenderUserID: msg.SenderUserID,
        ReceiverUserID: msg.ReceiverUserID,
        IsRead: msg.IsRead,
        IsArchive: msg.IsArchive,
        Attachments: msg.Attachments,
      }));

      setConversationMessages(formattedMessages);

      if (filter === "Unread") {
        const updatedMsgResponse = await fetchData({
          target: "listMessages",
          userOrder: "CreatedAt",
          orderDirection: "ASC",
          rowStart: 0,
          pageSize: 1000,
          filterTerms: {
            ConversationID: conversationID,
            ReceiverUserID: activeUser,
            IsRead: 0,
            IsArchive: 0,
          },
        });
        const filteredMessages =
          updatedMsgResponse.items?.filter(
            (msg) => msg.SenderUserID !== activeUser
          ) || [];
        const newUnreadCount = filteredMessages.length;
        console.log(
          `Updated unread count for ${user.emailID}:`,
          newUnreadCount
        );

        const updatedUsers = users.map((u) =>
          u.id === user.id ? { ...u, unreadCount: newUnreadCount } : u
        );
        setUsers(updatedUsers);
        setFilteredUsers((prev) =>
          prev.map((u) =>
            u.id === user.id ? { ...u, unreadCount: newUnreadCount } : u
          )
        );
      }

      await Promise.all(
        msgs.map(async (msg) => {
          try {
            if (msg.ReceiverUserID !== activeUser || msg.IsRead === 1) return;
            if (
              msg.ReceiverUserID === activeUser &&
              msg.IsRead === 0 &&
              filter === "Unread"
            ) {
              let msgDate = new Date(
                msg.CreatedAt.endsWith("Z")
                  ? msg.CreatedAt
                  : `${msg.CreatedAt}Z`
              );

              const now = new Date();
              const diffMs = now.getTime() - msgDate.getTime();

              const AFTER_MINUTES_MS = 1 * 60 * 1000; // 300 000 ms
              console.log(
                `Mark-read check | MsgID: ${msg.MessageID} | Filter: ${filter} | ` +
                  `DiffMs: ${diffMs} | Threshold: ${AFTER_MINUTES_MS} | MsgDate (UTC): ${msgDate.toISOString()} | Now (UTC): ${now.toISOString()}`
              );
              if (diffMs >= AFTER_MINUTES_MS) {
                await postQuery({
                  target: "updateMessages",
                  messagesInput: {
                    MessageID: msg.MessageID,
                    ConversationID: conversationID,
                    SenderUserID: msg.SenderUserID,
                    ReceiverUserID: msg.ReceiverUserID,
                    Content: msg.Content,
                    IsRead: 1,
                    IsArchive: msg.IsArchive,
                    Attachments: msg.Attachments,
                  },
                });
              }
            }
          } catch (error) {
            console.error(
              `Error updating IsRead for message ${msg.MessageID}:`,
              error
            );
          }
        })
      );
    } catch (error) {
      console.error("Error fetching messages:", error);
      setConversationMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (newMessage.trim() && selectedUser && !isSending) {
      setIsSending(true);
      try {
        let conversationID;
        const idResponse = await fetchData({
          target: "listConversations",
          userOrder: "LastMessageDate",
          orderDirection: "DESC",
          rowStart: 0,
          pageSize: 1,
          FirstAMTPLUserID: activeUser,
          SecondAMTPLUserID: selectedUser.emailID,
        });
        conversationID = idResponse?.items?.[0]?.ConversationID;

        if (!conversationID) {
          const createConvResponse = await postQuery({
            target: "createConversations",
            conversationsInput: {
              ConversationID: uuidv4(),
              FirstAMTPLUserID: activeUser,
              SecondAMTPLUserID: selectedUser.emailID,
            },
          });
          conversationID = createConvResponse.items.ConversationID;
        }

        const messageResponse = await postQuery({
          target: "createMessages",
          messagesInput: {
            MessageID: uuidv4(),
            ConversationID: conversationID,
            SenderUserID: activeUser,
            ReceiverUserID: selectedUser.emailID,
            Content: newMessage,
            IsRead: 0,
            IsArchive: 0,
            Attachments: null,
          },
        });

        const messageCreatedAt = messageResponse.items.CreatedAt;
        console.log("Message created at:", messageCreatedAt);
        await postQuery({
          target: "updateConversations",
          conversationsInput: {
            ConversationID: conversationID,
            FirstAMTPLUserID: activeUser,
            SecondAMTPLUserID: selectedUser.emailID,
            LastMessage: newMessage,
            LastMessageDate: messageCreatedAt,
          },
        });

        setNewMessage("");
        await fetchConversation(selectedUser);

        const updatedUser = {
          ...selectedUser,
          text: newMessage,
          date: formatDate(messageCreatedAt),
          rawDate: messageCreatedAt,
        };
        const filtered = users.filter((u) => u.id !== selectedUser.id);
        const updatedUsers = [updatedUser, ...filtered].sort((a, b) => {
          const dateA = a.rawDate ? new Date(a.rawDate) : new Date(0);
          const dateB = b.rawDate ? new Date(b.rawDate) : new Date(0);
          return dateB - dateA;
        });
        setUsers(updatedUsers);
        setFilteredUsers(updatedUsers);

        const mailMessage = `<p>New Message received from ${activeUser}.</p><p>Message:</p><p>${newMessage}</p>`;

        const bodyMail = {
          target: "financerfqmail",
          MailInput: {
            toAddresses: [
              selectedUser.emailID,
              "subhasish.mukherjee@agropreneurs.in",
            ],
            subject: `New Message`,
            msg: mailMessage,
          },
        };

        await postQuery(bodyMail, "backendops");
      } catch (error) {
        console.error("Error sending message:", error);
      } finally {
        setIsSending(false);
      }
    }
  };

  const confirmeArchiveMessage = (message) => async () => {
    try {
      console.log("archive message@@", message);

      await postQuery({
        target: "updateMessages",
        messagesInput: {
          MessageID: message.id,
          ConversationID: message.ConversationID,
          SenderUserID: message.SenderUserID,
          ReceiverUserID: message.ReceiverUserID,
          Content: message.text,
          IsRead: message.IsRead,
          IsArchive: 1,
          Attachments: message.Attachments,
        },
      });

      const msgResponse = await fetchData({
        target: "listMessages",
        userOrder: "CreatedAt",
        orderDirection: "ASC",
        rowStart: 0,
        pageSize: 1000,
        filterTerms: {
          ConversationID: message.ConversationID,
          ReceiverUserID: activeUser,
          IsRead: 0,
          IsArchive: 0,
        },
      });
      const filteredMessages =
        msgResponse.items?.filter((msg) => msg.SenderUserID !== activeUser) ||
        [];
      const newUnreadCount = filteredMessages.length;
      console.log(
        `Updated unread count after archiving for ${selectedUser.emailID}:`,
        newUnreadCount
      );

      const updatedUsers = users.map((u) =>
        u.id === selectedUser.id ? { ...u, unreadCount: newUnreadCount } : u
      );
      setUsers(updatedUsers);
      setFilteredUsers((prev) =>
        prev.map((u) =>
          u.id === selectedUser.id ? { ...u, unreadCount: newUnreadCount } : u
        )
      );

      setMenuOpen(null);
      if (selectedUser) {
        await fetchConversation(selectedUser);
      }
    } catch (error) {
      console.error("Error archiving message:", error);
    } finally {
      setModal((prev) => ({ ...prev, show: false }));
    }
  };

  const handleArchiveMessage = (message) => {
    console.log("Opening modal for message:", message);
    setModal({
      show: true,
      type: "warning",
      title: "Are you sure?",
      message: "Do you want to archive this message?",
      isConfirm: true,
      onConfirm: () => confirmeArchiveMessage(message)(),
      onCancel: () => setModal((prev) => ({ ...prev, show: false })),
    });
  };

  const confirmUnArchiveMessage = (message) => async () => {
    try {
      console.log("unarchive message@@", message);

      await postQuery({
        target: "updateMessages",
        messagesInput: {
          MessageID: message.id,
          ConversationID: message.ConversationID,
          SenderUserID: message.SenderUserID,
          ReceiverUserID: message.ReceiverUserID,
          Content: message.text,
          IsRead: message.IsRead,
          IsArchive: 0,
          Attachments: message.Attachments,
        },
      });

      setMenuOpen(null);
      if (selectedUser) {
        await fetchConversation(selectedUser);
      }
    } catch (error) {
      console.error("Error unarchiving message:", error);
    } finally {
      setModal((prev) => ({ ...prev, show: false }));
    }
  };

  const handleUnArchiveMessage = (message) => {
    console.log("Opening modal for message:", message);
    setModal({
      show: true,
      type: "warning",
      title: "Are you sure?",
      message: "Do you want to unarchive this message?",
      isConfirm: true,
      onConfirm: () => confirmUnArchiveMessage(message)(),
      onCancel: () => setModal((prev) => ({ ...prev, show: false })),
    });
  };

  const toggleMenu = (messageId) => {
    setMenuOpen(menuOpen === messageId ? null : messageId);
  };

  const truncateTo20Words = (text) => {
    const words = text.split(" ");
    if (words.length > 20) {
      return words.slice(0, 20).join(" ") + "...";
    }
    return text;
  };

  const getInitials = (firstName, lastName) => {
    const firstInitial = firstName?.[0] || "";
    const lastInitial = lastName?.[0] || "";
    return `${firstInitial}${lastInitial}`.toUpperCase();
  };

  const getAvatarColor = (name) => {
    const colors = [
      "#FF6B6B",
      "#4ECDC4",
      "#45B7D1",
      "#96CEB4",
      "#FFEEAD",
      "#D4A5A5",
      "#9B59B6",
      "#3498DB",
    ];
    const index = name.length % colors.length;
    return colors[index];
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Get buyer name for own messages
  const getBuyerName = () => {
    if (buyerInfo.length > 0) {
      const buyer = buyerInfo[0];
      return `${buyer.FirstName || ""} ${buyer.LastName || ""}`.trim() || "You";
    }
    return "You";
  };

  const getBuyerEmail = () => {
    if (buyerInfo.length > 0) {
      const buyer = buyerInfo[0];
      return buyer.EmailID || activeUser;
    }
    return activeUser;
  };

  const getBuyerInitials = () => {
    if (buyerInfo.length > 0) {
      const buyer = buyerInfo[0];
      return getInitials(buyer.FirstName, buyer.LastName);
    }
    return "";
  };

    useEffect(() => {
      if (messageAreaRef.current) {
        messageAreaRef.current.scrollTop = messageAreaRef.current.scrollHeight;
      }
    }, [conversationMessages]);

  return (
    <div className="buyer-dashboard2 card flex h-screen">
      <AlertModal
        type={modal.type}
        title={modal.title}
        message={modal.message}
        showModal={modal.show}
        onClose={handleModalClose}
        onConfirm={modal.onConfirm}
        onCancel={modal.onCancel}
        isConfirm={modal.isConfirm}
        buttonText="OK"
        autoClose={modal.autoClose}
      />
      <div className="chat-container flex flex-1">
        <div className="sidebar-message w-1/3 border-r flex flex-col">
          <div className="search-bar p-2 border-b sticky top-0 bg-white z-10">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search users..."
              className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex-1 overflow-y-auto">
            {filteredUsers.map((user) => (
              <div
                key={user.id}
                className="sidebar-item flex items-center p-2 hover:bg-gray-100 cursor-pointer"
                onClick={() => handleUserClick(user)}
              >
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold mr-3 sidebar-mes"
                  style={{ backgroundColor: getAvatarColor(user.sender) }}
                >
                  {getInitials(user.firstName, user.lastName)}
                </div>
                <div className="flex-1">
                  <div className="sender-name font-semibold">{user.sender}</div>
                  <div className="user-email text-xs text-gray-400 font-small">
                    {user.emailID}
                  </div>
                  <div className="message-preview text-sm text-gray-600 mt-1">
                    {truncateTo20Words(user.text)}
                  </div>
                </div>
                <div className="message-date text-sm text-gray-500 flex items-center">
                  {user.date}
                  {user.unreadCount > 0 && (
                    <span className="countbadge countbadge--circle">
                      {user.unreadCount}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="messaging-section flex-1 flex flex-col">
          {selectedUser ? (
            <>
              <div className="chat-header flex items-center p-2 border-b">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold mr-3 sidebar-mes"
                  style={{
                    backgroundColor: getAvatarColor(selectedUser.sender),
                  }}
                >
                  {getInitials(selectedUser.firstName, selectedUser.lastName)}
                </div>
                <div className="display-flex-space">
                  <div>
                    <div className="sender-name font-semibold">
                      {selectedUser.sender}
                    </div>
                    <div className="user-email text-sm text-gray-400 font-small">
                      {selectedUser.emailID}
                    </div>
                    <div className="status text-sm">
                      {selectedUser.companyName}
                    </div>
                  </div>
                  <div>
                    <div className="sender-name font-semibold">
                      {selectedUser.date}
                    </div>
                  </div>
                </div>
              </div>
              <div
                ref={messageAreaRef}
                className="message-area flex-1 p-2 overflow-y-auto flex flex-col gap-4"
              >
                {conversationMessages.map((msg, index) => {
                  const isOwn = msg.sender === activeUser;
                  const senderName = isOwn ? getBuyerName() : selectedUser.sender;
                  const senderEmail = isOwn ? getBuyerEmail() : selectedUser.emailID;
                  const firstName = isOwn 
                    ? buyerInfo[0]?.FirstName 
                    : selectedUser.firstName;
                  const lastName = isOwn 
                    ? buyerInfo[0]?.LastName 
                    : selectedUser.lastName;
                  const avatarColor = getAvatarColor(senderName);
                  const initials = getInitials(firstName, lastName);
                  const boldText = !isOwn && msg.IsRead === 0;
                  return (
                    <MessageBubble key={msg.id || index} isOwn={isOwn} elevation={0}>
                      <MessageHeader>
                        <Avatar 
                          sx={{ 
                            width: 32, 
                            height: 32, 
                            backgroundColor: avatarColor, 
                            fontSize: 14 
                          }}
                        >
                          {initials}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <UserName>{senderName}</UserName>
                          <UserEmail>{senderEmail}</UserEmail>
                          <Timestamp>{msg.date}</Timestamp>
                        </Box>
                      </MessageHeader>
                      <MessageText sx={{ fontWeight: boldText ? 600 : "normal" }}>
                        {msg.text}
                      </MessageText>
                    </MessageBubble>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="no-selection flex-1 flex items-center justify-center text-gray-500">
              Select a user to start chatting
            </div>
          )}
          {textmessage && selectedUser ? (
            <div className="input-area p-2 border-t flex gap-2">
              <textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Write a message..."
                rows="3"
                className="flex-1 p-2 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                onClick={handleSendMessage}
                className="submit-btn-more"
                disabled={isSending}
              >
                {isSending ? "Sending..." : "Send Reply"}
              </button>
            </div>
          ) : (
            <></>
          )}
        </div>
      </div>
    </div>
  );
};

export default AmMessage;